// Consumer financing (BL-005): "as low as $X a month" for an option, from the company's plans (the
// lender's terms it offers, in settings). A plan with no rate divides the total evenly; otherwise the
// payment is the standard amortised one. The lowest monthly payment wins, among the plans the total
// is big enough for; cents round up, so the line never promises less than the lender charges.

export interface FinancingPlan {
  months: number;
  /** Annual rate, as a percent (0 for a promotional plan). */
  aprPercent: number;
  /** The smallest amount the plan finances, in cents. */
  minCents?: number;
}

/** The monthly payment for a total over one plan, in cents. */
export function monthlyCents(totalCents: number, plan: FinancingPlan): number {
  const n = plan.months;
  const r = plan.aprPercent / 100 / 12;
  if (r === 0) return Math.ceil(totalCents / n);
  return Math.ceil((totalCents * r) / (1 - (1 + r) ** -n));
}

/** "As low as": the plan with the lowest monthly payment the total qualifies for; null with none. */
export function asLowAs(
  totalCents: number,
  plans: readonly FinancingPlan[],
): { monthlyCents: number; months: number; aprPercent: number } | null {
  let best: { monthlyCents: number; months: number; aprPercent: number } | null = null;
  for (const p of plans) {
    if (totalCents <= 0 || p.months < 1 || totalCents < (p.minCents ?? 0)) continue;
    const m = monthlyCents(totalCents, p);
    if (!best || m < best.monthlyCents)
      best = { monthlyCents: m, months: p.months, aprPercent: p.aprPercent };
  }
  return best;
}

/** The company's financing setting (payments.financing): its lender, apply link and plans. */
export interface FinancingSetting {
  lender: string;
  applyUrl: string | null;
  plans: FinancingPlan[];
}

/**
 * The apply link, if it's one a customer may be sent to: https only (QA-666). Anything else
 * (javascript:, data:, http:) is never shown as a link, even one saved before the setting refused it.
 */
export function safeApplyUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    return new URL(url).protocol === 'https:' && /^https:\/\//i.test(url) ? url : null;
  } catch {
    return null;
  }
}

/** "$1,321.00": cents as dollars, to the cent. */
const dollars = (cents: number) =>
  `$${(cents / 100).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * "As low as $132.10 a month with Financeit (120 months, 9.99 %)", or null with no plan for the total:
 * the same words in the office, the portal and on the iPad.
 */
export function asLowAsWords(totalCents: number, v: FinancingSetting | null | undefined): string | null {
  if (!v) return null;
  const best = asLowAs(totalCents, v.plans);
  if (!best) return null;
  return `As low as ${dollars(best.monthlyCents)} a month with ${v.lender} (${best.months} months, ${best.aprPercent} %)`;
}
